import React, { useEffect, useState } from 'react';
import { Bot, Clock, Sparkles } from 'lucide-react';
import {
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatCard from '../components/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import api, { analyticsApi } from '../lib/api';

const RANGE_OPTIONS = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

const TOOL_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#64748b'];

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '12px',
  color: '#e2e8f0',
};

function getDateWindow(rangeKey) {
  const option = RANGE_OPTIONS.find((range) => range.key === rangeKey) || RANGE_OPTIONS[1];
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (option.days - 1));

  return {
    days: option.days,
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function durationBetween(start, end) {
  const seconds = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000)
  );
  return Number.isFinite(seconds) ? seconds : 0;
}

function formatDuration(seconds) {
  const totalMinutes = Math.round((seconds || 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function normalizeToolName(value = '') {
  const tool = value.toLowerCase();
  if (tool.includes('chatgpt') || tool.includes('openai')) return 'ChatGPT';
  if (tool.includes('claude')) return 'Claude';
  if (tool.includes('gemini')) return 'Gemini';
  if (tool.includes('copilot')) return 'Copilot';
  if (tool.includes('cursor')) return 'Cursor';
  return 'Other';
}

function buildToolDistribution(summaryTools = []) {
  const toolMap = {};

  summaryTools.forEach((tool) => {
    const label = normalizeToolName(tool.tool_name);
    toolMap[label] = (toolMap[label] || 0) + (tool.sec || 0);
  });

  return Object.entries(toolMap)
    .map(([name, value]) => ({ name, value }))
    .sort((first, second) => second.value - first.value);
}

function buildUsageTrend(events = [], days = 30) {
  const series = Array.from({ length: days }).map((_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (days - index - 1));
    const key = date.toISOString().slice(0, 10);
    return { date: key, label: formatDayLabel(key), sessions: 0 };
  });

  const pointMap = series.reduce((accumulator, point) => {
    accumulator[point.date] = point;
    return accumulator;
  }, {});

  events.forEach((event) => {
    const key = String(event.ts_start || '').slice(0, 10);
    if (!pointMap[key]) return;
    pointMap[key].sessions += 1;
  });

  return series;
}

export default function AIAnalytics() {
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [data, setData] = useState({
    summary: null,
    events: [],
  });

  useEffect(() => {
    setTablePage(1);
  }, [range]);

  useEffect(() => {
    const load = async () => {
      const { startDate, endDate } = getDateWindow(range);
      setLoading(true);

      const [summaryResult, exportResult] = await Promise.allSettled([
        analyticsApi.ai({ start_date: startDate, end_date: endDate }),
        api.get('/export/ai_tools', {
          params: { date_from: startDate, date_to: endDate, page: 1, page_size: 1000 },
        }),
      ]);

      if (summaryResult.status === 'rejected') {
        toast.error('Failed to load AI analytics.');
      }

      if (exportResult.status === 'rejected') {
        toast.error('Detailed AI tool events are unavailable for this account.');
      }

      setData({
        summary: summaryResult.status === 'fulfilled' ? summaryResult.value.data : null,
        events: exportResult.status === 'fulfilled' ? exportResult.value.data?.items || [] : [],
      });
      setLoading(false);
    };

    load();
  }, [range]);

  const { days } = getDateWindow(range);
  const toolDistribution = buildToolDistribution(data.summary?.tools || []);
  const usageTrend = buildUsageTrend(data.events, days);
  const totalSessions = data.events.length;
  const totalUsageSeconds = data.events.reduce(
    (sum, event) => sum + durationBetween(event.ts_start, event.ts_end),
    0
  );
  const mostUsedTool = toolDistribution[0]?.name || 'No data';
  const pageSize = 10;
  const recentEvents = [...data.events].sort(
    (first, second) => new Date(second.ts_start).getTime() - new Date(first.ts_start).getTime()
  );
  const paginatedEvents = recentEvents.slice((tablePage - 1) * pageSize, tablePage * pageSize);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 text-white" data-testid="ai-analytics-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">AI Oversight</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-white">AI Tool Usage</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Session distribution, daily trend, and recent AI tool usage across sanctioned tools.
          </p>
        </div>

        <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800/80 p-1">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`rounded-lg px-4 py-2 text-sm transition ${
                range === option.key
                  ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              onClick={() => setRange(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <>
          <LoadingSkeleton type="card" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <LoadingSkeleton type="chart" />
            <LoadingSkeleton type="chart" />
          </div>
          <LoadingSkeleton type="table" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              title="Total AI Sessions"
              value={totalSessions}
              change={totalSessions / Math.max(days, 1) * 10}
              changeLabel="sessions per day"
              icon={<Sparkles className="h-5 w-5" />}
              color="purple"
            />
            <StatCard
              title="Most Used Tool"
              value={mostUsedTool}
              change={toolDistribution[0] ? (toolDistribution[0].value / 3600) * 10 : 0}
              changeLabel="hours tracked"
              icon={<Bot className="h-5 w-5" />}
              color="blue"
            />
            <StatCard
              title="Avg Daily Usage"
              value={formatDuration(totalUsageSeconds / Math.max(days, 1))}
              change={totalUsageSeconds / Math.max(days, 1) / 3600}
              changeLabel="daily AI time"
              icon={<Clock className="h-5 w-5" />}
              color="green"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">AI Tool Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {toolDistribution.length ? (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr),220px] lg:items-center">
                    <div className="h-[340px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={toolDistribution}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={72}
                            outerRadius={112}
                            paddingAngle={4}
                          >
                            {toolDistribution.map((item, index) => (
                              <Cell
                                key={item.name}
                                fill={TOOL_COLORS[index % TOOL_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(value) => formatDuration(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {toolDistribution.map((item, index) => (
                        <div key={item.name} className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <span
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: TOOL_COLORS[index % TOOL_COLORS.length] }}
                              />
                              <span className="text-sm text-slate-200">{item.name}</span>
                            </div>
                            <span className="text-sm text-slate-400">{formatDuration(item.value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Sparkles className="h-8 w-8" />}
                    title="No AI sessions"
                    description="AI usage events are empty for this selected range."
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Usage Trend</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {usageTrend.some((point) => point.sessions > 0) ? (
                  <div className="h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={usageTrend}>
                        <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Line
                          type="monotone"
                          dataKey="sessions"
                          stroke="#8b5cf6"
                          strokeWidth={2.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Clock className="h-8 w-8" />}
                    title="No trend data"
                    description="Detailed AI session export is unavailable for this range."
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="text-white">AI Tool Events</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <DataTable
                columns={[
                  {
                    key: 'employee_name',
                    label: 'Employee',
                    render: (value) => value || 'Unknown',
                  },
                  {
                    key: 'tool_name',
                    label: 'Tool',
                    render: (value) => normalizeToolName(value),
                  },
                  {
                    key: 'duration',
                    label: 'Duration',
                    render: (_, row) => formatDuration(durationBetween(row.ts_start, row.ts_end)),
                  },
                  {
                    key: 'context',
                    label: 'Window Title',
                    render: (value) => value || '—',
                  },
                  {
                    key: 'ts_start',
                    label: 'Timestamp',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
                data={paginatedEvents}
                loading={loading}
                emptyMessage="No AI tool events are available for the current range."
                pagination={{
                  page: tablePage,
                  pageSize,
                  total: recentEvents.length,
                  onPageChange: setTablePage,
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
